import { useFormik } from "formik";
import { t } from "i18next";
import { DateTime } from "luxon";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Toolbar } from "primereact/toolbar";
import { classNames } from "primereact/utils";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { useApis } from "../../api";
import PageLoader from "../../components/PageLoader";
import Panel from "../../components/Panel";
import Table, { TableHeader, TableSettings } from "../../components/Table";
import { NotificationContext } from "../../context/NotificationContext";
import { Actions, Subjects, useAuth } from "../../hooks/useAuth";
import { localizeDate, timezoneOptions } from "../../hooks/useDate";
import Layout from "../../layout/layout";
import { Place } from "../Places";

export enum EventStatus {
  programmed = "programmed",
  inProgress = "inProgress",
  closed = "closed",
  draft = "draft",
}

export type Event = {
  id: string;
  virtual: boolean;
  name: string;
  description: string;
  place_id: string;
  date: string;
  startTime: string;
  status: EventStatus;
  campaignId: string;
};

type Error = {
  name?: string;
  virtual?: boolean;
  description?: string;
  place_id?: string;
  date?: string;
  startTime?: string;
  status?: string;
  campaignId?: string;
};

export const statuses = [
  {
    label: t("app.events.statuses.programmed") ?? "Programmed",
    value: EventStatus.programmed,
  },
  {
    label: t("app.events.statuses.inProgress") ?? "In Progress",
    value: EventStatus.inProgress,
  },
  {
    label: t("app.events.statuses.closed") ?? "Closed",
    value: EventStatus.closed,
  },
  {
    label: t("app.events.statuses.draft") ?? "Draft",
    value: EventStatus.draft,
  },
];

export default function EventComponent() {
  const {
    getEvents,
    getEvent,
    createEvent,
    updateEvent,
    deleteEvent,
    getPlaces,
  } = useApis();
  const navigate = useNavigate();
  const { checkUserPermission } = useAuth();
  const { addNotification } = useContext(NotificationContext);
  const formik = useFormik({
    initialValues: {
      name: "",
      virtual: false,
      description: "",
      place_id: "",
      date: "",
      startTime: new Date(new Date().setHours(0, 0, 0, 0))?.toISOString(),
      status: EventStatus.draft,
      campaignId: "",
    },
    validate: (data) => {
      let errors: Error = {};

      if (!data.name) {
        errors.name = t("common.requiredField", {
          field: t("app.events.fields.name"),
        });
      }

      if (!data.place_id) {
        errors.place_id = t("common.requiredField", {
          field: t("app.events.fields.place"),
        });
      }

      if (!data.date) {
        errors.date = t("common.requiredField", {
          field: t("app.events.fields.date"),
        });
      }

      if (!data.startTime) {
        errors.startTime = t("common.requiredField", {
          field: t("app.events.fields.startTime"),
        });
      }

      if (!data.status) {
        errors.status = t("common.requiredField", {
          field: t("app.events.fields.status"),
        });
      }

      if (!data.campaignId) {
        errors.campaignId = t("common.requiredField", {
          field: t("app.events.fields.campaignId"),
        });
      }

      return errors;
    },
    onSubmit: async (data) => {
      if (selectedEventId) {
        mUpdateEvent.mutate({ id: selectedEventId, ...data });
      } else {
        mCreateEvent.mutate({
          ...data,
        });
      }
    },
  });

  const [visibleSidebar, setVisibleSidebar] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [isEventFetching, setIsEventFetching] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [tableSettings, setTableSettings] = useState<TableSettings>({});
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const fields: TableHeader[] = useMemo(
    () => [
      {
        field: "name",
        header: t("app.events.fields.name"),
        sortable: true,
        filterable: true,
      },
      {
        field: "virtual",
        header: t("app.events.fields.virtual"),
        sortable: true,
      },
      {
        field: "place",
        header: t("app.events.fields.place"),
        sortable: false,
        filterable: true,
        beField: "place.name",
      },
      {
        field: "status",
        header: t("app.events.fields.status"),
        sortable: true,
        filterable: true,
      },
      {
        field: "date",
        header: t("app.events.fields.date"),
        sortable: false,
        filterable: false,
      },
    ],
    [],
  );

  useEffect(() => {
    const filters: TableSettings["filters"] = {};
    fields.forEach((field) => {
      if (field.filterable) {
        filters[field.field] = {
          value: "",
          matchMode: "contains",
          beField: field.beField || field.field,
        };
      }
    });
    setTableSettings({
      sorting: {
        field: fields[0].field,
        order: -1,
      },
      filters,
      pagination: {
        first: 0,
        rows: 10,
      },
    });
  }, [fields]);

  const qEvents = useQuery(["events", tableSettings], getEvents, {
    onSuccess: (res: any) => {
      if (res.totalRecords) {
        setTotalRecords(parseInt(res.totalRecords));
      }
    },
    onSettled: () => {
      if (isFirstLoad) {
        setIsFirstLoad(false);
      }
    },
  });

  const { data, isFetching } = qEvents;

  useQuery(
    ["event", selectedEventId, isEventFetching],
    () => getEvent(selectedEventId),
    {
      enabled: !!selectedEventId && isEventFetching,
      onSuccess: (data) => {
        if (isEventFetching) {
          const {
            event: {
              name,
              description,
              place_id,
              date,
              status,
              startTime,
              virtual,
              campaignId,
            },
          } = data;
          formik.setValues({
            name: name,
            virtual: virtual,
            description: description,
            place_id: place_id,
            date: date,
            status: status,
            startTime: startTime,
            campaignId: campaignId,
          });

          setIsEventFetching(false);
        }
      },
    },
  );

  const mCreateEvent = useMutation(createEvent, {
    onSuccess: () => {
      qEvents.refetch();
      addNotification({
        title: t("common.creationSuccess"),
        type: "success",
      });
    },
    onError: () => {
      addNotification({
        title: t("common.genericError"),
        type: "error",
      });
    },
    onSettled: () => {
      setVisibleSidebar(false);
      formik.resetForm();
    },
  });

  const mUpdateEvent = useMutation(updateEvent, {
    onSuccess: () => {
      qEvents.refetch();
      addNotification({
        title: t("common.updateSuccess"),
        type: "success",
      });
    },
    onError: () => {
      addNotification({
        title: t("common.genericError"),
        type: "error",
      });
    },
    onSettled: () => {
      setSelectedEventId("");
      setVisibleSidebar(false);
      formik.resetForm();
    },
  });

  const mDeleteEvent = useMutation(deleteEvent, {
    onSuccess: () => {
      qEvents.refetch();
      addNotification({
        title: t("common.deleteSuccess"),
        type: "success",
      });
    },
    onError: () => {
      addNotification({
        title: t("common.genericError"),
        type: "error",
      });
    },
  });

  const qPlaces = useQuery("places", getPlaces);

  const { data: placesData } = qPlaces;

  const places = useMemo(
    () =>
      placesData?.data?.map((place: Place) => ({
        value: place.id,
        label: place.name,
      })),
    [placesData],
  );

  const tableItems: Event[] = useMemo(() => {
    const events = data?.data || [];
    return events.map((event: any) => ({
      id: event.id,
      name: event.name,
      virtual: event.virtual ? t("common.yes") : t("common.no"),
      place: event.place.name,
      date: new Date(event.date).toLocaleDateString(),
      status: statuses.find((status: any) => status.value === event.status)
        ?.label,
      realStatus: event.status,
    }));
  }, [data?.data]);

  const placeTimezone = useMemo(() => {
    const place = placesData?.data?.find(
      (place: Place) => place.id === formik.values.place_id,
    );
    const timezone = place?.timeZone || "Europe/Rome";

    return timezone;
  }, [formik.values.place_id, placesData]);

  const formattedDate = useMemo(() => {
    return localizeDate(
      DateTime.fromISO(formik.values.date).toJSDate(),
      placeTimezone,
    );
  }, [formik.values.date, placeTimezone]);

  const formattedTime = useMemo(() => {
    return localizeDate(
      DateTime.fromISO(formik.values.startTime).toJSDate(),
      placeTimezone,
    );
  }, [formik.values.startTime, placeTimezone]);

  const updateDate = async (e: any) => {
    const inputDate = e.value;
    const hour = DateTime.fromISO(formik.values.startTime)
      .toJSDate()
      .getHours();
    const date = DateTime.fromJSDate(inputDate)
      .setZone(placeTimezone)
      .set({ hour })
      .setZone("UTC");

    await formik.setFieldValue("date", date.toISO());

    const startTime = DateTime.fromISO(formik.values.startTime)
      .setZone(placeTimezone)
      .set({ year: date.year, month: date.month, day: date.day })
      .setZone("UTC");

    formik.setFieldValue("startTime", startTime.toISO());
  };

  const updateTime = (e: any) => {
    const inputTime = e.value;
    const hour = inputTime.getHours();
    const minute = inputTime.getMinutes();
    const date = DateTime.fromJSDate(inputTime)
      .setZone(placeTimezone)
      .set({ hour, minute })
      .setZone("UTC");

    formik.setFieldValue("startTime", date.toISO());
  };

  const isFormFieldValid = (name: keyof Error) =>
    !!(formik.touched[name] && formik.errors[name]);

  const getFormErrorMessage = (name: keyof Error) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    );
  };

  const rightToolbarTemplate = () => {
    if (checkUserPermission(Subjects.events, Actions.create)) {
      return (
        <React.Fragment>
          <div className="my-2">
            <Button
              label={t("common.new")}
              icon="pi pi-plus"
              onClick={() => {
                setSelectedEventId("");
                formik.resetForm();
                setVisibleSidebar(true);
              }}
            />
          </div>
        </React.Fragment>
      );
    } else {
      return null;
    }
  };

  const getRowActions = () => {
    const rowActions: any = {};

    if (checkUserPermission(Subjects.events, Actions.update)) {
      rowActions["onItemEdit"] = {
        command: (item: Event) => {
          setSelectedEventId(item.id);
          setIsEventFetching(true);
          setVisibleSidebar(true);
        },
      };
    }

    if (checkUserPermission(Subjects.events, Actions.delete)) {
      rowActions["onItemDelete"] = {
        command: (item: any) => {
          mDeleteEvent.mutate(item.id);
        },
        condition: (item: any) => {
          return item.realStatus !== EventStatus.inProgress;
        },
      };
    }

    return rowActions;
  };

  return (
    <Layout>
      {isFirstLoad ? (
        <PageLoader />
      ) : (
        <div className="grid crud-demo">
          <div className="col-12">
            <div className="card">
              <Toolbar
                className="border-0 mb-4"
                style={{ backgroundColor: "transparent" }}
                right={rightToolbarTemplate}
                left={() => (
                  <span className="text-2xl font-semibold">
                    {t("app.events.title")}
                  </span>
                )}
              ></Toolbar>
              <Table
                items={tableItems}
                fields={fields}
                rowActions={getRowActions()}
                loading={isFetching}
                onRowClick={(item: any) => {
                  const event = data?.data?.find(
                    (event: any) => event.id === item.data.id,
                  );

                  if (event?.virtual) {
                    navigate(`/events/${item.data.id}/virtual`);
                  } else {
                    navigate(`/events/${item.data.id}`);
                  }
                }}
                settings={tableSettings}
                setSettings={setTableSettings}
                totalRecords={totalRecords}
                pagination
                selectionMode={true}
              />
              <Panel
                visible={visibleSidebar}
                setVisible={setVisibleSidebar}
                loading={isEventFetching}
                title={
                  selectedEventId
                    ? t("common.editEntity", {
                        entity: t("entities.event", { count: 1 }),
                        context: "male",
                      })
                    : t("common.newEntity", {
                        entity: t("entities.event", { count: 1 }),
                        context: "male",
                      })
                }
                onHide={() => {
                  setTimeout(() => {
                    setSelectedEventId("");
                    formik.resetForm();
                  }, 500);
                }}
                onSave={() => formik.handleSubmit()}
              >
                <form>
                  <div className="flex flex-column mx-auto md:mx-0">
                    <ul className="list-none m-0 p-0">
                      <div className="mb-3">
                        {!!formik.values.place_id && (
                          <Message
                            text={`${t(
                              "app.events.timezoneMessage",
                            )} ${timezoneOptions.find(
                              (el) => el.value === placeTimezone,
                            )?.label}`}
                            severity="info"
                          />
                        )}
                      </div>
                      {!selectedEventId && (
                        <li>
                          <div className="flex  gap-4">
                            <label
                              htmlFor="virtual"
                              className={classNames({
                                "font-semibold mb-2": true,
                                "p-error": isFormFieldValid("virtual"),
                              })}
                            >
                              {t("app.events.fields.virtual")}
                            </label>
                            <Checkbox
                              id="virtual"
                              name="virtual"
                              className={classNames({
                                "p-invalid": isFormFieldValid("virtual"),
                              })}
                              checked={formik.values.virtual}
                              onChange={formik.handleChange}
                            />
                          </div>
                          {getFormErrorMessage("virtual")}
                        </li>
                      )}
                      <li className="mt-3">
                        <label
                          htmlFor="name"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("name"),
                          })}
                        >
                          {t("app.events.fields.name")}
                        </label>
                        <InputText
                          id="name"
                          name="name"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("name"),
                          })}
                          value={formik.values.name}
                          onChange={formik.handleChange}
                        />
                        {getFormErrorMessage("name")}
                      </li>

                      <li className="mt-3">
                        <label
                          htmlFor="date"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("date"),
                          })}
                        >
                          {t("app.events.fields.date")}
                        </label>
                        <Calendar
                          inputId="date"
                          name="date"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("date"),
                          })}
                          value={formattedDate}
                          onChange={updateDate}
                          showIcon
                        />
                        {getFormErrorMessage("date")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="startTime"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("startTime"),
                          })}
                        >
                          {t("app.events.fields.startTime")}
                        </label>
                        <Calendar
                          inputId="startTime"
                          name="startTime"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("startTime"),
                          })}
                          value={formattedTime}
                          onChange={updateTime}
                          showIcon
                          timeOnly
                        />
                        {getFormErrorMessage("startTime")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="place_id"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("place_id"),
                          })}
                        >
                          {t("app.events.fields.place")}
                        </label>
                        <Dropdown
                          inputId="place_id"
                          name="place_id"
                          value={formik.values.place_id}
                          options={places}
                          optionLabel="label"
                          optionValue="value"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("place_id"),
                          })}
                          onChange={(e) => {
                            formik.setFieldValue("place_id", e.value);
                          }}
                        />
                        {getFormErrorMessage("place_id")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="status"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("status"),
                          })}
                        >
                          {t("app.events.fields.status")}
                        </label>
                        <Dropdown
                          inputId="status"
                          name="status"
                          value={formik.values.status}
                          options={statuses}
                          disabled={true}
                          optionLabel="label"
                          optionValue="value"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("status"),
                          })}
                          onChange={(e) => {
                            formik.setFieldValue("status", e.value);
                          }}
                        />
                        {getFormErrorMessage("status")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="campaignId"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("campaignId"),
                          })}
                        >
                          {t("app.events.fields.campaignId")}
                        </label>
                        <InputText
                          id="campaignId"
                          name="campaignId"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("campaignId"),
                          })}
                          value={formik.values.campaignId}
                          onChange={formik.handleChange}
                        />
                        {getFormErrorMessage("campaignId")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="description"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("description"),
                          })}
                        >
                          {t("app.events.fields.description")}
                        </label>
                        <InputTextarea
                          id="description"
                          name="description"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("description"),
                          })}
                          value={formik.values.description}
                          onChange={formik.handleChange}
                        />
                        {getFormErrorMessage("description")}
                      </li>
                    </ul>
                  </div>
                </form>
              </Panel>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
